import { useEffect, useState } from "react";
import type { User } from "@supabase/auth-js";
import { isAuthEnabled, supabase } from "./supabaseClient.js";

export interface SessionState {
  user: User | null;
  loading: boolean;
}

/**
 * Tracks the current Supabase auth session. When auth isn't configured (tests,
 * offline build) it resolves immediately to a logged-out state.
 */
export function useSession(): SessionState {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(isAuthEnabled);

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }

    let active = true;
    void supabase.auth.getSession().then(
      ({ data }) => {
        if (!active) return;
        setUser(data.session?.user ?? null);
        setLoading(false);
      },
      () => {
        // Session restore failed (abnormal storage/lock error) — proceed
        // signed-out rather than leaving `loading` stuck true, which would
        // gate the app's list fetching forever.
        if (active) setLoading(false);
      }
    );

    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, []);

  return { user, loading };
}
